// Карточка объекта: колонка детали с пятью вкладками. Потолок — восемь вкладок
// (правило 12), здесь четыре, и пятая появится только вместе с табелем.
//
// Вкладка живёт в адресе (?tab=norm): ссылка на норматив объекта открывается у коллеги
// нормативом, а не обзором. Состояние вкладок держит Radix Tabs, значение ему даёт адрес —
// одно состояние, а не два расходящихся.

import { RiBuilding4Line, RiPencilLine } from '@remixicon/react';
import { useNavigate } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import * as Button from '@/shared/ui/button';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Badge from '@/shared/ui/badge';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { CardTab as CardTabTrigger, CardTabsList } from '@/shared/ui/card-tabs';
import * as TabMenu from '@/shared/ui/tab-menu-horizontal';

import { useSiteQuery } from '@/shared/org/queries';
import type { CardTab, SitesRoute } from '../route';
import { cardTab, openEditSiteHref, sitesHref } from '../route';

import { CloseSiteDialog } from './CloseSiteDialog';
import { NormTab } from './NormTab';
import { OverviewTab } from './OverviewTab';
import { PostsTab } from './PostsTab';
import { PayrollTemplateTab } from './PayrollTemplateTab';
import { StaffingTab } from './StaffingTab';

const TAB_TITLES: Readonly<Record<CardTab, string>> = {
  overview: 'Обзор',
  posts: 'Посты',
  norm: 'Норматив',
  staffing: 'Укомплектованность',
  payroll: 'Ведомость',
};

export function SiteCard({ siteId, route }: { siteId: string; route: SitesRoute }) {
  const navigate = useNavigate();
  const правитьМожно = can(useAuthStore((state) => state.permissions), 'core.dictionary.edit');
  const query = useSiteQuery(siteId);
  const tab = cardTab(route.search.tab);

  return (
    <TabMenu.Root
      value={tab}
      onValueChange={(next) =>
        navigate(sitesHref(route.view, { ...route.search, tab: next }, siteId))
      }
      className='flex h-full min-w-0 flex-1 flex-col'
    >
      <DetailPane
        title={
          <span className='flex items-center gap-2'>
            <RiBuilding4Line className='size-5 shrink-0 text-text-soft-400' />
            <span className='truncate'>{query.data?.name ?? 'Объект'}</span>
            {query.data !== undefined && !query.data.is_active && (
              <Badge.Root variant='lighter' color='gray' size='medium'>
                закрыт
              </Badge.Root>
            )}
          </span>
        }
        actions={
          query.data === undefined ? undefined : (
            <>
              {/* Правка объекта появилась 06.09.2026: до неё карточку можно было только
                  закрыть, а город и регион не заводились даже при создании — объект молча
                  выпадал из отборов по региону и отчётов по городам. */}
              {правитьМожно && (
                <Button.Root
                  type='button'
                  variant='neutral'
                  mode='stroke'
                  size='xsmall'
                  onClick={() => navigate(openEditSiteHref(route, siteId))}
                >
                  <Button.Icon as={RiPencilLine} />
                  Править
                </Button.Root>
              )}
              {query.data.is_active && <CloseSiteDialog site={query.data} />}
            </>
          )
        }
        tabs={
          <CardTabsList>
            {(Object.keys(TAB_TITLES) as CardTab[]).map((key) => (
              <CardTabTrigger key={key} value={key}>
                {TAB_TITLES[key]}
              </CardTabTrigger>
            ))}
          </CardTabsList>
        }
      >
        <ScreenState
          data={query.data}
          error={query.error}
          subject='карточка объекта'
          isEmpty={() => false}
          onRetry={() => void query.refetch()}
          loading={
            <div className='flex flex-col gap-3 p-5'>
              <Skeleton className='h-24 w-full' />
              <Skeleton className='h-24 w-full' />
            </div>
          }
          empty={null}
        >
          {(site) => (
            <>
              <TabMenu.Content value='overview'>
                <OverviewTab site={site} />
              </TabMenu.Content>
              <TabMenu.Content value='posts'>
                <PostsTab site={site} route={route} />
              </TabMenu.Content>
              <TabMenu.Content value='norm'>
                <NormTab site={site} route={route} />
              </TabMenu.Content>
              <TabMenu.Content value='staffing'>
                <StaffingTab site={site} route={route} />
              </TabMenu.Content>
              <TabMenu.Content value='payroll'>
                <PayrollTemplateTab site={site} />
              </TabMenu.Content>
            </>
          )}
        </ScreenState>
      </DetailPane>
    </TabMenu.Root>
  );
}
